import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

interface SaleLine {
  id: string;
  product_name: string;
  format_name: string;
  qty: number;
  cash_price_at_sale: number;
  card_price_at_sale: number;
  discount_applied: number;
}

interface Sale {
  id: string;
  status: string;
  subtotal_cash: number;
  subtotal_card: number;
  tax_cash: number;
  tax_card: number;
  total_cash: number;
  total_card: number;
  lines: SaleLine[] | null;
}

export default function CustomerDisplay() {
  const [sale, setSale] = useState<Sale | null>(null);

  const poll = useCallback(async () => {
    try {
      const response = await axios.get("/api/display/register-1");
      setSale(response.data || null);
    } catch {
      setSale(null);
    }
  }, []);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [poll]);

  const lines = sale?.lines || [];
  const totalCash = Number(sale?.total_cash || 0);
  const totalCard = Number(sale?.total_card || 0);

  if (!sale || lines.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: "var(--rbw-navy)" }}>
        <div className="text-center">
          <img src="/images/logo-primary.jpg" alt="Rieker Bottle Works" className="w-64 mx-auto rounded-xl shadow-2xl mb-8" />
          <p className="text-3xl font-light mb-2" style={{ color: "var(--rbw-gray-light)" }}>Welcome!</p>
          <div className="mt-4 px-6 py-3 rounded-xl inline-block" style={{ background: "rgba(246,190,0,0.1)", border: "1px solid var(--rbw-gold-dark)" }}>
            <span className="text-lg" style={{ color: "var(--rbw-gold)" }}>
              Pay with cash or debit and save on every purchase
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" style={{ background: "var(--rbw-navy)" }}>
      <div className="px-6 py-3 flex justify-between items-center" style={{ background: "var(--rbw-navy-dark)" }}>
        <img src="/images/logo-secondary.jpg" alt="RBW" className="h-8 rounded" />
        <span style={{ color: "var(--rbw-gray)" }}>
          {lines.length} item{lines.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {lines.map((line) => (
          <div key={line.id} className="flex justify-between items-center py-3" style={{ borderBottom: "1px solid rgba(196,191,182,0.2)" }}>
            <div>
              <div className="text-lg" style={{ color: "var(--rbw-gray-light)" }}>{line.product_name}</div>
              <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>{line.format_name} x{line.qty}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold" style={{ color: "var(--rbw-gold)" }}>
                ${(Number(line.cash_price_at_sale) * line.qty).toFixed(2)}
              </div>
              <div className="text-sm" style={{ color: "var(--rbw-blue)" }}>
                ${(Number(line.card_price_at_sale) * line.qty).toFixed(2)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-6 py-6" style={{ background: "var(--rbw-navy-dark)" }}>
        <div className="grid grid-cols-2 gap-6">
          <div className="rounded-2xl p-6" style={{ background: "rgba(246,190,0,0.08)", border: "2px solid var(--rbw-gold-dark)" }}>
            <div className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--rbw-gold)" }}>
              Cash / Debit Price
            </div>
            <div className="text-5xl font-bold" style={{ color: "var(--rbw-gold)" }}>${totalCash.toFixed(2)}</div>
            <div className="text-sm mt-2 font-medium" style={{ color: "var(--rbw-gold-light)" }}>
              You save ${(totalCard - totalCash).toFixed(2)}
            </div>
          </div>
          <div className="rounded-2xl p-6" style={{ background: "rgba(139,184,232,0.08)", border: "2px solid var(--rbw-blue-dark)" }}>
            <div className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--rbw-blue)" }}>
              Credit Card Price
            </div>
            <div className="text-5xl font-bold" style={{ color: "var(--rbw-blue)" }}>${totalCard.toFixed(2)}</div>
            <div className="text-sm mt-2" style={{ color: "var(--rbw-blue-light)" }}>includes service fee</div>
          </div>
        </div>
      </div>
    </div>
  );
}
